/*
 * Copyright (C) Motorola Solutions, INC.
 * All Rights Reserved.
 */

import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { selectClusterNames } from '../+state/configuration.selectors';
import { ClusterUrlProviderService } from '../../core/services/cluster-url-provider.service';
import { HttpClient } from '@angular/common/http';
import { catchError, filter, map } from 'rxjs/operators';
import { heartbeatRequest } from '../+state/configuration.actions';

@Injectable({
    providedIn: 'root'
})
export class CtcHeartbeatService {
    public static readonly HEARTBEAT_ENDPOINT = `/api/sync-server-time`;
    private static readonly HEARTBEAT_INTERVAL = 10000;
    private static readonly HEARTBEAT_LEVEL_1_INTERVAL = 10000 * 6;
    private static readonly HEARTBEAT_LEVEL_2_INTERVAL = 10000 * 12;
    private static readonly HEARTBEAT_LEVEL_1_THRESHOLD = 9;
    private static readonly HEARTBEAT_LEVEL_2_THRESHOLD = 30;
    private timerIds: { [clusterName: string]: number } = {};
    private clusterNames: string[] = [];
    private clusterIntervals: { [clusterName: string]: number } = {};
    private healthcheckErrorCount: { [clusterName: string]: number } = {};
    private doHeartbeat = (clusterName: string) => this.store.dispatch(heartbeatRequest({ clusterName: clusterName }));

    constructor(
        private http: HttpClient,
        private store: Store,
        private clusterUrlProviderService: ClusterUrlProviderService
    ) {
        this.store
            .select(selectClusterNames)
            .pipe(filter((val) => Boolean(val.length)))
            .subscribe((clusterNames) => {
                this.clusterNames = clusterNames;
                this.clusterNames.forEach((clusterName) => {
                    this.clusterIntervals[clusterName] = CtcHeartbeatService.HEARTBEAT_INTERVAL;
                    this.healthcheckErrorCount[clusterName] = 0;
                    this.doHeartbeat(clusterName);
                    this.scheduleHeartbeats(clusterName, this.clusterIntervals[clusterName]);
                });

            });
    }

    scheduleHeartbeats(clusterName: string, clusterInterval: number) {
        window.clearTimeout(this.timerIds[clusterName]);
        this.timerIds[clusterName] = window.setInterval(() => this.doHeartbeat(clusterName), clusterInterval);
    }

    public ping(clusterName: string) {
        return this.http.get<number>(this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, CtcHeartbeatService.HEARTBEAT_ENDPOINT), {
            observe: 'body',
            responseType: 'json'
        }).pipe(
            map(() => {
                this.healthcheckErrorCount[clusterName] = 0;
                if (this.clusterIntervals[clusterName] !== CtcHeartbeatService.HEARTBEAT_INTERVAL) {
                    this.clusterIntervals[clusterName] = CtcHeartbeatService.HEARTBEAT_INTERVAL;
                    this.scheduleHeartbeats(clusterName, this.clusterIntervals[clusterName]);
                }
            }),
            catchError((error) => {
                const consecutiveErrors = ++this.healthcheckErrorCount[clusterName];
                if (consecutiveErrors >= CtcHeartbeatService.HEARTBEAT_LEVEL_2_THRESHOLD) {
                    this.clusterIntervals[clusterName] = CtcHeartbeatService.HEARTBEAT_LEVEL_2_INTERVAL;
                    this.scheduleHeartbeats(clusterName, this.clusterIntervals[clusterName]);
                    throw error;
                }
                if (consecutiveErrors >= CtcHeartbeatService.HEARTBEAT_LEVEL_1_THRESHOLD) {
                    this.clusterIntervals[clusterName] = CtcHeartbeatService.HEARTBEAT_LEVEL_1_INTERVAL;
                    this.scheduleHeartbeats(clusterName, this.clusterIntervals[clusterName]);
                }
                throw error;
            }));
    }
}
